import type { AppShellNavSection } from '@avila-roffe/components-web';
import { useQuery } from '@tanstack/react-query';
import type { LucideIcon } from 'lucide-react';
import {
  BookOpen,
  ChefHat,
  Circle,
  LayoutDashboard,
  MessageSquare,
  Settings,
  ShoppingCart,
  Table2,
  Users,
  UsersRound,
  UtensilsCrossed,
} from 'lucide-react';
import { STATIC_NAV } from '../lib/staticNav';
import { useAuthStore } from '../stores/authStore';

// ─── Types ────────────────────────────────────────────────────────────────────

// Shape returned by /rbff/v1/restaurant/nav
interface NavItem {
  key: string;
  label: string;
  path: string;
  icon?: string;
  children?: NavItem[];
}

// ─── Icon map ─────────────────────────────────────────────────────────────────

const iconMap: Record<string, LucideIcon> = {
  LayoutDashboard,
  UtensilsCrossed,
  BookOpen,
  ChefHat,
  Users,
  ShoppingCart,
  Settings,
  MessageSquare,
  Table2,
  UsersRound,
  dashboard: LayoutDashboard,
  restaurant: UtensilsCrossed,
  menu_book: BookOpen,
  lunch_dining: ChefHat,
  local_bar: ShoppingCart,
  table_bar: Table2,
  people: UsersRound,
  receipt_long: ShoppingCart,
  manage_accounts: Users,
  settings: Settings,
};

function resolveIcon(name?: string): LucideIcon {
  if (!name) return Circle;
  return iconMap[name] ?? Circle;
}

// ─── Mapper ───────────────────────────────────────────────────────────────────

function mapItems(items: NavItem[]): AppShellNavSection['items'] {
  return items.map((item) => ({
    label: item.label,
    href: item.path,
    icon: resolveIcon(item.icon),
    items: item.children?.map((child) => ({
      label: child.label,
      href: child.path,
    })),
  }));
}

function mapToNavSections(items: NavItem[]): AppShellNavSection[] {
  return [{ items: mapItems(items) }];
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export interface UseNavigationResult {
  nav: AppShellNavSection[];
  isLoading: boolean;
  error: Error | null;
}

export function useNavigation(): UseNavigationResult {
  const accessToken = useAuthStore((s) => s.accessToken);

  const { data, isLoading, error } = useQuery<NavItem[], Error>({
    queryKey: ['navigation'],
    queryFn: async () => {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (accessToken) {
        headers.Authorization = `Bearer ${accessToken}`;
      }

      const res = await fetch('/rbff/v1/restaurant/nav', { headers });
      if (!res.ok) {
        throw new Error(`Navigation fetch failed: ${res.status}`);
      }
      return res.json() as Promise<NavItem[]>;
    },
    staleTime: 5 * 60_000,
    retry: false,
  });

  if (error || (!isLoading && !data)) {
    // BFF not ready — fall back to static nav
    return { nav: STATIC_NAV, isLoading: false, error: error };
  }

  return {
    nav: data?.length ? mapToNavSections(data) : STATIC_NAV,
    isLoading,
    error: null,
  };
}
